import { Box } from '@mui/material';
import { darken } from '@mui/material/styles';
import { LineChartProps, lineClasses } from '@mui/x-charts';
import { MarkShape } from '@mui/x-charts/models';
import React, { useId, useMemo } from 'react';

import LineChart from 'components/charts/line-chart';
import ChartLegend from 'components/charts/chart-legend';
import { SERIES_HIGHLIGHT } from 'components/charts/constants/chart-defaults';
import { useChartGradient } from 'components/charts/hooks/use-chart-gradient';
import { useChartLegendVisibility } from 'components/charts/hooks/use-chart-legend-visibility';
import { useChartAnimation } from 'components/charts/hooks/use-chart-animation';
import { useLegendHighlight } from 'components/charts/hooks/use-legend-highlight';
import { useSeriesVisibility } from 'components/charts/hooks/use-series-visibility';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import DataLabChartHiddenSeriesOverlay from 'views/playground/features/data-lab/components/data-lab-chart-hidden-series-overlay';
import DataLabChartLegendVisibilityMenu from 'views/playground/features/data-lab/components/data-lab-chart-legend-visibility-menu';
import DataLabChartTooltip from 'views/playground/features/data-lab/components/data-lab-chart-tooltip';
import { TimeSeriesData } from 'views/playground/features/data-lab/types/chart-series.type';
import { useMetricFormatters } from 'views/playground/features/data-lab/hooks/use-metric-formatters';

// Keep marker shapes tied to source-series indices so paint sorting does not
// change the legend marker.
const MARK_SHAPES: MarkShape[] = ['circle', 'square', 'diamond', 'cross', 'star', 'triangle', 'wye'];

type Props = Pick<LineChartProps, 'height'> & {
  data: TimeSeriesData;
  // Axis clicks select a time bucket rather than a domain entity.
  onAxisSelect?: (pointIndex: number | null) => void;
};

// Axis selection uses `onAxisSelect`, not the item-selection contract.
const TrendLineChart: React.FC<Props> = ({ data, height = 320, onAxisSelect }) => {
  const { formatKg } = useMetricFormatters();
  const palette = useChartPalette();
  const chartSkipAnimation = useChartAnimation();
  const { visible: legendVisible, toggle: toggleLegend } = useChartLegendVisibility();

  // Pin colors and marker shapes before filtering so hiding a series does not
  // change the remaining series' visual identity.
  const coloredSeries = useMemo(
    () =>
      data.series.map((series, index) => ({
        ...series,
        color: palette.series.categorical[index % palette.series.categorical.length],
        shape: MARK_SHAPES[index % MARK_SHAPES.length],
      })),
    [data.series, palette.series.categorical]
  );

  const { hiddenIds, toggle, visibleSeries } = useSeriesVisibility(coloredSeries);
  const { highlightedItem, onHighlightChange, onLegendItemHover, onLegendItemLeave } = useLegendHighlight();
  // The card cannot see per-series visibility, so this chart owns the
  // all-series-hidden message.
  const allSeriesHidden = coloredSeries.length > 0 && visibleSeries.length === 0;
  // Single-series fill still gets the softer gradient fade below; two or more
  // visible series get the layered "ridge" fill instead (see paintOrderedSeries).
  const soloVisibleSeries = visibleSeries.length === 1 ? visibleSeries[0] : undefined;

  // Opaque fills paint over earlier series. Sorting by the final value creates
  // a ridge without blending colors. This fixture's series do not cross; data
  // with crossing series would need a different fill strategy.
  const paintOrderedSeries = useMemo(
    () =>
      [...visibleSeries].sort((a, b) => {
        const aLast = a.data[a.data.length - 1] ?? 0;
        const bLast = b.data[b.data.length - 1] ?? 0;
        return bLast - aLast;
      }),
    [visibleSeries]
  );

  // A translucent fade is safe when only one series is visible.
  const gradient = useChartGradient(soloVisibleSeries?.color ?? palette.series.categorical[0], [
    { offset: '0%', opacity: 0.32 },
    { offset: '100%', opacity: 0 },
  ]);

  // Ridge fills stay opaque to prevent overlapping series from blending into
  // colors outside the selected palette.
  const ridgeGradientBaseId = useId().replace(/:/g, '');
  const ridgeGradientDefs = (
    <defs>
      {coloredSeries.map((series, index) => (
        <linearGradient key={series.id} id={`${ridgeGradientBaseId}-${index}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={series.color} stopOpacity={1} />
          <stop offset="100%" stopColor={darken(series.color, 0.5)} stopOpacity={1} />
        </linearGradient>
      ))}
    </defs>
  );
  const ridgeFillFor = (seriesId: string): string | undefined => {
    const index = coloredSeries.findIndex((series) => String(series.id) === seriesId);
    return index === -1 ? undefined : `url(#${ridgeGradientBaseId}-${index})`;
  };

  const areaFillSx: Record<string, { fill: string | undefined }> = {};
  if (soloVisibleSeries) {
    areaFillSx[`& .${lineClasses.area}[data-series="${soloVisibleSeries.id}"]`] = { fill: gradient.fill };
  } else {
    visibleSeries.forEach((series) => {
      areaFillSx[`& .${lineClasses.area}[data-series="${series.id}"]`] = { fill: ridgeFillFor(String(series.id)) };
    });
  }

  // Derive the y-axis domain from every series so its ticks remain when all
  // series are hidden.
  const yAxisMax = useMemo(() => {
    const values = coloredSeries.flatMap((series) => series.data).filter((value): value is number => value !== null);
    return values.length > 0 ? Math.max(...values) : undefined;
  }, [coloredSeries]);

  return (
    <>
      <Box display="flex" justifyContent="flex-end">
        <DataLabChartLegendVisibilityMenu visible={legendVisible} onToggle={toggleLegend} />
      </Box>
      <LineChart
        height={height}
        skipAnimation={chartSkipAnimation}
        hideLegend
        // Zoom and pan require an unlicensed MUI X Pro chart.
        xAxis={[{ data: data.points, scaleType: 'point', label: 'Week' }]}
        // Explicit bounds keep the scale and tick labels stable when all
        // series are hidden.
        yAxis={[
          {
            scaleType: 'linear',
            min: 0,
            max: yAxisMax,
            label: 'Biomass (kg)',
          },
        ]}
        series={paintOrderedSeries.map((series) => ({
          ...series,
          area: true,
          showMark: true,
          valueFormatter: (value: number | null) => (value === null ? '' : formatKg(value)),
          ...SERIES_HIGHLIGHT,
        }))}
        highlightedItem={highlightedItem}
        onHighlightChange={onHighlightChange}
        onAxisClick={(_event, axisData) => onAxisSelect?.(axisData?.dataIndex ?? null)}
        slots={{ noDataOverlay: DataLabChartHiddenSeriesOverlay, tooltip: DataLabChartTooltip }}
        sx={{
          ...areaFillSx,
          // Keep the hidden-series message at full contrast while dimming the
          // chart labels. This state-dependent rule cannot live in the theme.
          ...(allSeriesHidden && { '& text:not(.chart-hidden-series-overlay-text)': { opacity: 0.4 } }),
        }}
      >
        {gradient.defs}
        {ridgeGradientDefs}
      </LineChart>
      {legendVisible && (
        <ChartLegend
          items={coloredSeries.map((series) => ({
            id: String(series.id),
            label: series.label,
            color: series.color,
            shape: series.shape,
          }))}
          hiddenIds={hiddenIds}
          onToggle={toggle}
          onItemHover={onLegendItemHover}
          onItemLeave={onLegendItemLeave}
        />
      )}
    </>
  );
};

export default TrendLineChart;
